import { useCallback, useRef, useState } from 'react';
import {
  GRID_COLUMNS,
  GRID_ROW_HEIGHT,
  clampColSpan,
  clampColStart,
  getComponentGridMin,
  getGridPlacement,
} from '../utils/defaults';
import { gridRowHeightFromElement } from '../utils/designKit';
import {
  collectSnapTargets,
  snapPlacement,
  snapResizePlacement,
} from '../utils/snapGuides';

const DRAG_THRESHOLD = 5;

export function getGridMetrics(gridEl) {
  const rect = gridEl.getBoundingClientRect();
  return {
    rect,
    colWidth: rect.width / GRID_COLUMNS,
    rowHeight: gridRowHeightFromElement(gridEl) ?? GRID_ROW_HEIGHT,
  };
}

export function pointerToCol(clientX, metrics) {
  const relX = clientX - metrics.rect.left;
  return Math.floor(relX / metrics.colWidth) + 1;
}

export function pointerToRow(clientY, metrics) {
  const relY = clientY - metrics.rect.top;
  return Math.max(0, Math.floor(relY / metrics.rowHeight));
}

export function placementFromPointer(clientX, clientY, gridEl, base, grabOffsetCol, grabOffsetRow) {
  const m = getGridMetrics(gridEl);
  const colStart = clampColStart(pointerToCol(clientX, m) - grabOffsetCol, base.colSpan);
  const row = Math.max(0, pointerToRow(clientY, m) - grabOffsetRow);
  return {
    colStart,
    colSpan: base.colSpan,
    row,
    rowSpan: base.rowSpan ?? 1,
  };
}

/**
 * Drag/resize coordonné au niveau éditeur — snap d’alignement inclus.
 * @param {object} opts
 * @param {string} opts.viewport
 * @param {(sectionId: string) => any[]} opts.getSectionComponents
 * @param {Function} opts.onMoveComponent
 * @param {Function} opts.onTransferComponent
 */
export function useEditorGridDrag({
  viewport,
  getSectionComponents,
  onMoveComponent,
  onTransferComponent,
}) {
  const gridRefs = useRef(new Map());
  const previewRef = useRef(null);
  const guidesRef = useRef([]);
  const suppressClickRef = useRef(false);

  const [session, setSession] = useState(null);

  const registerGrid = useCallback((sectionId, el) => {
    if (el) gridRefs.current.set(sectionId, el);
    else gridRefs.current.delete(sectionId);
  }, []);

  const findGridAt = useCallback((clientX, clientY) => {
    for (const [sectionId, el] of gridRefs.current.entries()) {
      const rect = el.getBoundingClientRect();
      if (
        clientX >= rect.left &&
        clientX <= rect.right &&
        clientY >= rect.top &&
        clientY <= rect.bottom
      ) {
        return { sectionId, gridEl: el };
      }
    }
    return null;
  }, []);

  const patchSession = useCallback((patch) => {
    setSession((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const setLivePreview = useCallback(
    (preview, targetSectionId, guides = []) => {
      previewRef.current = preview;
      guidesRef.current = guides;
      patchSession({ preview, targetSectionId, guides, isInteracting: true });
    },
    [patchSession],
  );

  const endSession = useCallback(() => {
    previewRef.current = null;
    guidesRef.current = [];
    setSession(null);
  }, []);

  const startMove = useCallback(
    (fromSectionId, comp, e) => {
      if (e.button !== 0) return;
      const originGrid = gridRefs.current.get(fromSectionId);
      if (!originGrid) return;

      e.preventDefault();
      e.stopPropagation();

      const originX = e.clientX;
      const originY = e.clientY;
      const metrics = getGridMetrics(originGrid);
      const base = getGridPlacement(comp, viewport);
      const grabOffsetCol = pointerToCol(originX, metrics) - base.colStart;
      const grabOffsetRow = pointerToRow(originY, metrics) - base.row;
      let moved = false;

      setSession({
        mode: 'move',
        comp,
        fromSectionId,
        targetSectionId: fromSectionId,
        preview: null,
        guides: [],
        isInteracting: false,
      });

      const onMove = (ev) => {
        if (!moved && Math.hypot(ev.clientX - originX, ev.clientY - originY) < DRAG_THRESHOLD) {
          return;
        }
        moved = true;

        const hit = findGridAt(ev.clientX, ev.clientY);
        const gridEl = hit?.gridEl || originGrid;
        const targetSectionId = hit?.sectionId || fromSectionId;
        const raw = placementFromPointer(
          ev.clientX,
          ev.clientY,
          gridEl,
          base,
          grabOffsetCol,
          grabOffsetRow,
        );

        const peers = getSectionComponents?.(targetSectionId) || [];
        const targets = collectSnapTargets(peers, comp.id, viewport);
        const { placement, guides } = snapPlacement(raw, targets);

        setLivePreview(placement, targetSectionId, guides);
      };

      const onUp = (ev) => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);

        if (moved && previewRef.current) {
          suppressClickRef.current = true;
          window.setTimeout(() => {
            suppressClickRef.current = false;
          }, 0);

          const hit = findGridAt(ev.clientX, ev.clientY);
          const targetSectionId = hit?.sectionId || fromSectionId;
          const placement = previewRef.current;

          if (targetSectionId !== fromSectionId) {
            onTransferComponent(fromSectionId, targetSectionId, comp.id, placement);
          } else {
            onMoveComponent(fromSectionId, comp.id, placement);
          }
        }

        endSession();
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [
      viewport,
      findGridAt,
      getSectionComponents,
      onMoveComponent,
      onTransferComponent,
      setLivePreview,
      endSession,
    ],
  );

  const startResize = useCallback(
    (sectionId, comp, handle, e) => {
      if (e.button !== 0) return;
      const grid = gridRefs.current.get(sectionId);
      if (!grid) return;

      e.preventDefault();
      e.stopPropagation();

      const base = getGridPlacement(comp, viewport);
      const mins = getComponentGridMin(comp.type);

      setSession({
        mode: 'resize',
        comp,
        fromSectionId: sectionId,
        targetSectionId: sectionId,
        preview: { ...base },
        guides: [],
        isInteracting: true,
      });
      previewRef.current = { ...base };

      const onMove = (ev) => {
        const m = getGridMetrics(grid);
        const relX = ev.clientX - m.rect.left;
        const relY = ev.clientY - m.rect.top;

        let { colStart, colSpan, row, rowSpan } = base;
        rowSpan = rowSpan ?? 1;

        if (handle.includes('e')) {
          const endCol = Math.min(
            GRID_COLUMNS,
            Math.max(colStart, Math.ceil(relX / m.colWidth)),
          );
          colSpan = clampColSpan(Math.max(mins.colSpan, endCol - colStart + 1));
        }
        if (handle.includes('s')) {
          const endRow = Math.max(row, Math.floor(relY / m.rowHeight));
          rowSpan = Math.max(mins.rowSpan, endRow - row + 1);
        }

        const raw = { colStart, colSpan, row, rowSpan };
        const peers = getSectionComponents?.(sectionId) || [];
        const targets = collectSnapTargets(peers, comp.id, viewport);
        const { placement, guides } = snapResizePlacement(raw, handle, targets);
        let nextColSpan = clampColSpan(Math.max(mins.colSpan, placement.colSpan));
        let nextRowSpan = Math.max(mins.rowSpan, placement.rowSpan);
        // Ne pas agrandir via le snap quand l’utilisateur réduit le bloc.
        if (handle.includes('e')) {
          nextColSpan = clampColSpan(
            Math.max(mins.colSpan, Math.min(nextColSpan, raw.colSpan)),
          );
        }
        if (handle.includes('s')) {
          nextRowSpan = Math.max(mins.rowSpan, Math.min(nextRowSpan, raw.rowSpan));
        }

        setLivePreview(
          {
            ...placement,
            colSpan: nextColSpan,
            rowSpan: nextRowSpan,
          },
          sectionId,
          guides,
        );
      };

      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);

        if (previewRef.current) {
          onMoveComponent(sectionId, comp.id, previewRef.current);
        }
        endSession();
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [viewport, getSectionComponents, onMoveComponent, setLivePreview, endSession],
  );

  return {
    registerGrid,
    session,
    suppressClickRef,
    startMove,
    startResize,
    activeId: session?.comp?.id ?? null,
    isInteracting: session?.isInteracting ?? false,
  };
}
